import React, { ChangeEvent, FC, Fragment, useState } from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { QueryResult } from 'react-apollo';
import { Paper } from '@material-ui/core';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { pull, uniq, intersectionWith } from 'lodash';
import { compose } from 'recompose';

import { ClientInvitationStatus } from 'api';
import { EmptyState, Queries } from 'components';
import { adminPeopleInvite } from 'paths';

import Header from '../components/Invitations/Header';
import PendingColumns from '../components/Invitations/PendingColumns';
import AcceptedColumns from '../components/Invitations/AcceptedColumns';
import Loader from '../components/Invitations/Loader';
import VirtualizedTable from '../VirtualizedTable';

interface IPropsEnhanced extends RouteComponentProps, WithStyles<typeof styles> {}

const LIMIT = 24;

const styles = (theme: Theme) =>
  createStyles({
    container: {
      marginBottom: theme.spacing(3),
    },
    column: {
      borderTop: `1px solid ${theme.custom.palette.grey3}`,
      borderBottom: `1px solid ${theme.custom.palette.grey3}`,
    },
  });

const enhancers = compose<IPropsEnhanced, {}>(withRouter, withStyles(styles));

const Invitations: FC<IPropsEnhanced> = ({ classes, history }) => {
  const [filter, setFilter] = useState(ClientInvitationStatus.Active);
  const [total, setTotal] = useState(null);
  const [invitations, setInvitations] = useState([]);

  const [checkedIds, setCheckedIds] = useState([]);
  const [allChecked, setAllChecked] = useState(false);

  const isPending = filter === ClientInvitationStatus.Active;
  const canResend = isPending && !!checkedIds.length;
  const variables = { invitation_status: filter, limit: LIMIT };

  function getColumns() {
    if (!isPending) {
      return AcceptedColumns({ className: classes.column });
    }

    return PendingColumns({
      checkedIds,
      className: classes.column,
      isAllChecked: allChecked,
      onAllChecked: handleAllChecked,
      onChecked: handleChecked,
    });
  }

  function handleChecked(event: ChangeEvent<HTMLInputElement>) {
    const { checked, name } = event.target;

    checked ? checkedIds.unshift(name) : pull(checkedIds, name);
    setCheckedIds(uniq(checkedIds));
  }

  function handleAllChecked(event: ChangeEvent<HTMLInputElement>) {
    const { checked } = event.target;
    const ids = checked ? invitations.map(invitation => invitation.id) : [];

    setCheckedIds(ids);
    setAllChecked(checked);
  }

  function handleFilterChange(value: ClientInvitationStatus) {
    setTotal(null);
    setFilter(value);
  }

  function handleResend() {
    const selectedInvitations = intersectionWith(
      invitations,
      checkedIds,
      (first, second) => first.id === second,
    );
    const uniqueEmails = uniq(selectedInvitations.map(invitation => invitation.email_address));

    history.push(adminPeopleInvite, { emails: uniqueEmails });
  }

  function handleQueryCompleted(data: QueryResult['data']) {
    const { getClientInvitationsByClient } = data;

    setTotal(getClientInvitationsByClient.count);
    setInvitations(getClientInvitationsByClient.client_invitations);
  }

  return (
    <Paper>
      <Header
        filter={filter}
        total={total}
        checkedTotal={checkedIds.length}
        canResend={canResend}
        onChange={handleFilterChange}
        onResend={handleResend}
      />
      <Queries.GetClientInvitationsByClient
        fetchPolicy="network-only"
        onCompleted={handleQueryCompleted}
        loader={<Loader rows={8} />}
        variables={variables}>
        {({ count }, { onLoadMore, exhausted }) => (
          <Fragment>
            <VirtualizedTable
              columns={getColumns()}
              spacing={1}
              outerBorderWidth={2}
              headerHeight={50}
              rowHeight={50}
              total={count}
              rowCount={invitations.length}
              headerClassName={classes.column}
              isRowLoaded={({ index }: { index: number }) => !!invitations[index]}
              rowGetter={({ index }: { index: number }) => invitations[index]}
              onLoadMoreRows={onLoadMore}
              noRowsRenderer={() => <EmptyState name="suspendedUsers" />}
            />
            {!exhausted && <Loader rows={1} />}
          </Fragment>
        )}
      </Queries.GetClientInvitationsByClient>
    </Paper>
  );
};

export default enhancers(Invitations);
